import { NgTemplateOutlet } from '@angular/common'
import { Component, computed, EventEmitter, inject, input, Input, Output, ViewChild } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { Router, RouterModule } from '@angular/router'
import { SubscriptionPlanType } from '@ftr/external-organization-contracts'
import { DropdownMenuComponent, IconComponent, MenuItemButton, MenuItemGroup, MenuItemLink } from '@ftr/foundation'
import { DropdownMenuLegacyComponent } from '@ftr/menus'
import { AppPaths } from '@ftr/routing-paths'
import { OrganizationState, ResetOrganizationStateAction } from '@ftr/ui-organization'
import { UserAvatarComponent, UserState } from '@ftr/ui-user'
import { Store } from '@ngxs/store'
import { map } from 'rxjs'
import { SideNavDisplayMode } from '../../navigation'

const USER_AND_ORG_MENU_ITEMS = [
  { label: 'Browse organizations', path: [`/${AppPaths.Organizations}`], icon: 'Grid' },
  {
    label: 'Create organization',
    path: [`/${AppPaths.SelectPlanTier}`],
    queryParams: { planType: SubscriptionPlanType.Organization, countryCode: 'US' },
    icon: 'Add',
  },
] satisfies MenuItemLink[]

@Component({
  selector: 'ftr-user-and-org-dropdown',
  templateUrl: './user-and-org-dropdown.component.html',
  styleUrls: ['./user-and-org-dropdown.component.css'],
  host: {
    '[attr.theme]': 'theme()',
  },
  imports: [DropdownMenuComponent, UserAvatarComponent, IconComponent, RouterModule, NgTemplateOutlet],
})
export class UserAndOrgDropdownComponent {
  private readonly store = inject(Store)
  private readonly router = inject(Router)

  @Input() collapsed: boolean = false
  @Input() displayMode: SideNavDisplayMode = 'DEFAULT'
  theme = input<'light' | 'dark'>('dark')
  @Output() dropdownOpened = new EventEmitter<MenuItemGroup[]>()

  @ViewChild(DropdownMenuLegacyComponent) protected dropdown: DropdownMenuLegacyComponent

  protected readonly currentUser = toSignal(
    this.store.select(UserState.user).pipe(map(user => user.unwrapOr(undefined))),
  )

  protected readonly currentOrganization = toSignal(this.store.select(OrganizationState.currentOrganization))

  protected readonly appPaths = AppPaths

  protected readonly menuItems = computed<MenuItemGroup[]>(() => {
    const user = this.currentUser()
    return [
      ...(user ? [{ key: 'individual', items: [this.individualMenuItem(user)] }] : []),
      { key: 'organizations', items: [...USER_AND_ORG_MENU_ITEMS] },
    ]
  })

  protected readonly componentSelector = 'ftr-user-and-org-dropdown'

  toggle(): void {
    this.dropdown?.toggle()
  }

  private individualMenuItem(user: { givenName: string; familyName: string }): MenuItemButton {
    return {
      label: `${user.givenName} ${user.familyName}`,
      icon: 'Person',
      action: () => {
        this.store.dispatch(new ResetOrganizationStateAction())
        this.router.navigate([`/${AppPaths.Individual}`])
      },
    }
  }
}
